import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, Copy, ArrowLeft, ExternalLink, Loader2, Info } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { BACKEND_URL, getFaq, getPixel, getRichtext, getGallery, listMedia } from "@/lib/api";
import FAQView from "@/components/previews/FAQView";
import RichTextView from "@/components/previews/RichTextView";
import GalleryView from "@/components/previews/GalleryView";

const LABELS = {
  faq: "FAQ Accordion",
  ps_pixel: "PortalStudio Tracking Pixel",
  ga_pixel: "Google Analytics Pixel",
  richtext: "Rich Text Page",
  gallery: "Photo Gallery",
};

function CopyButton({ value, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <Button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      className="h-11 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 transition-all duration-200 min-w-[120px]"
    >
      {copied ? <Check className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
      {copied ? "Copied" : "Copy"}
    </Button>
  );
}

function normalizePath(raw) {
  if (!raw) return "";
  let v = raw.trim();
  if (!v) return "";
  // Strip origin if the user pasted a full URL
  try {
    if (/^https?:\/\//i.test(v)) {
      const u = new URL(v);
      v = u.pathname + u.search + u.hash;
    }
  } catch { /* ignore */ }
  if (!v.startsWith("/")) v = "/" + v;
  return v;
}

export default function Success() {
  const { type, id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagePath, setPagePath] = useState("");

  const isPixel = type === "pixel" || type === "ps_pixel" || type === "ga_pixel";
  const urls = useMemo(() => {
    if (isPixel) {
      return {
        embed: `${BACKEND_URL}/api/embed/pixel/${id}.gif`,
        iframe: null,
        script: null,
      };
    }
    const htmlUrl = `${BACKEND_URL}/api/embed/${type}/${id}`;
    const scriptUrl = `${BACKEND_URL}/api/embed/${type}/${id}.js`;
    return {
      embed: htmlUrl, iframe: htmlUrl, script: scriptUrl,
    };
  }, [type, id, isPixel]);

  useEffect(() => {
    let cancelled = false;
    const fetcher = type === "faq" ? getFaq
                   : type === "richtext" ? getRichtext
                   : type === "gallery" ? getGallery
                   : getPixel; // covers ps_pixel + ga_pixel
    fetcher(id)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => toast.error("Component not found"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [type, id]);

  const scriptSnippet = urls.script
    ? `<script src="${urls.script}" defer></script>`
    : "";
  const iframeSnippet = urls.iframe
    ? `<iframe src="${urls.iframe}" width="100%" height="640" style="border:0;" loading="lazy"></iframe>`
    : "";

  // For PS pixels: bake the page path into the URL so Veracross-style
  // widgets (which render a plain <img> with no referrerpolicy) still
  // report the full page path.
  const normalizedPath = normalizePath(pagePath);
  const pixelPageUrl = isPixel && normalizedPath
    ? `${urls.embed}?u=${encodeURIComponent(normalizedPath)}`
    : urls.embed;

  const pixelSnippet = isPixel
    ? `<img src="${pixelPageUrl}" referrerpolicy="unsafe-url" width="1" height="1" alt="" style="display:none" />`
    : "";

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-950 mb-6" data-testid="success-back-link">
          <ArrowLeft className="h-4 w-4" /> Back home
        </Link>

        <div className="border border-zinc-200 bg-white rounded-md p-8 md:p-10 animate-fade-up">
          <div className="flex items-start gap-4 mb-8">
            <div className="h-12 w-12 rounded-md grid place-items-center bg-green-50 text-green-700 border border-green-200">
              <Check className="h-5 w-5" />
            </div>
            <div>
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Ready</div>
              <h1 className="font-display text-3xl tracking-tight font-semibold text-zinc-950 mt-1">
                Your {LABELS[type] || "component"} is live
              </h1>
              <p className="text-zinc-600 mt-2 text-sm">
                Share this URL or paste the snippet. It works anywhere.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Embed URL</div>
            <div className="flex gap-2">
              <Input
                value={pixelPageUrl}
                readOnly
                data-testid="generated-url-input"
                className="font-mono text-sm h-11"
                onFocus={(e) => e.target.select()}
              />
              <CopyButton value={pixelPageUrl} testid="copy-url-btn" />
            </div>
            <a
              href={pixelPageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-950"
              data-testid="open-embed-link"
            >
              Open in new tab <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          {isPixel && (
            <div className="mt-8 border border-amber-200 bg-amber-50/60 rounded-md p-5" data-testid="pixel-page-url-builder">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-md grid place-items-center bg-amber-100 text-amber-700 border border-amber-200 shrink-0">
                  <Info className="h-4 w-4" />
                </div>
                <div className="flex-1">
                  <div className="text-xs tracking-[0.2em] uppercase font-bold text-amber-800">
                    Veracross Portals: bake the page path into the URL
                  </div>
                  <p className="text-[13px] text-zinc-700 mt-2 leading-relaxed">
                    Veracross&apos; <strong>Photo Link</strong> component stores only a URL &mdash; it strips HTML
                    attributes like <code className="font-mono text-[11px] bg-white/70 px-1 rounded">referrerpolicy</code>,
                    so the browser falls back to its default and sends only the origin (no path). To capture the
                    exact page this pixel lives on, enter its path below and paste the generated URL into the
                    Photo Link&apos;s <em>Photo URL</em> field. Create one pixel URL per page.
                  </p>
                  <div className="mt-4 space-y-2">
                    <label className="text-[11px] tracking-[0.15em] uppercase font-bold text-zinc-500">
                      Page path on your portal
                    </label>
                    <div className="flex gap-2">
                      <Input
                        value={pagePath}
                        onChange={(e) => setPagePath(e.target.value)}
                        placeholder="/parent/learning-support"
                        data-testid="pixel-page-path-input"
                        className="font-mono text-sm h-11 bg-white"
                      />
                      <CopyButton value={pixelPageUrl} testid="copy-pixel-page-url-btn" />
                    </div>
                    <p className="text-[11px] text-zinc-500 mt-1">
                      Paste a path like <span className="font-mono">/parent/learning-support</span> or a full URL &mdash;
                      we keep only the path. The generated URL above updates live.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isPixel && (
            <div className="mt-8">
              <Tabs defaultValue="url">
                <TabsList className="bg-zinc-100">
                  <TabsTrigger value="url" data-testid="snippet-url-tab">
                    URL (for Notion, Veracross, etc.)
                  </TabsTrigger>
                  <TabsTrigger value="script" data-testid="snippet-script-tab">
                    &lt;script&gt; tag
                  </TabsTrigger>
                  <TabsTrigger value="iframe" data-testid="snippet-iframe-tab">
                    &lt;iframe&gt;
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="url" className="mt-3">
                  <div className="flex gap-2">
                    <Input value={urls.embed} readOnly className="font-mono text-sm h-11" />
                    <CopyButton value={urls.embed} testid="copy-link-btn" />
                  </div>
                  <p className="text-xs text-zinc-500 mt-3">
                    Paste this plain URL into any platform that auto-renders links —
                    Iframely-based embeds (Veracross, Notion, Substack, Ghost,
                    WordPress, Medium). No script, no iframe required.
                  </p>
                </TabsContent>
                <TabsContent value="script" className="mt-3">
                  <div className="flex gap-2 items-start">
                    <pre className="flex-1 bg-zinc-950 text-zinc-100 p-4 rounded-md text-xs font-mono overflow-x-auto">
                      <code>{scriptSnippet}</code>
                    </pre>
                    <CopyButton value={scriptSnippet} testid="copy-script-btn" />
                  </div>
                  <p className="text-xs text-zinc-500 mt-3">
                    Paste directly in HTML on your own site — renders inline via Shadow DOM, no iframe.
                  </p>
                </TabsContent>
                <TabsContent value="iframe" className="mt-3">
                  <div className="flex gap-2 items-start">
                    <pre className="flex-1 bg-zinc-950 text-zinc-100 p-4 rounded-md text-xs font-mono overflow-x-auto">
                      <code>{iframeSnippet}</code>
                    </pre>
                    <CopyButton value={iframeSnippet} testid="copy-iframe-btn" />
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          )}

          {isPixel && (
            <div className="mt-8">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-1">HTML Image Snippet</div>
              <p className="text-xs text-zinc-500 mb-3">
                For platforms that accept raw HTML (email, WordPress, Ghost, custom CMS). For <strong>Veracross Portals</strong>,
                paste the <em>Embed URL</em> above into the Photo Link component instead &mdash; raw HTML attributes don&apos;t survive Veracross.
              </p>
              <div className="flex gap-2 items-start">
                <pre className="flex-1 bg-zinc-950 text-zinc-100 p-4 rounded-md text-xs font-mono overflow-x-auto">
                  <code>{pixelSnippet}</code>
                </pre>
                <CopyButton value={pixelSnippet} testid="copy-pixel-snippet-btn" />
              </div>
            </div>
          )}
        </div>

        {/* Preview */}
        <div className="mt-10" data-testid="success-preview">
          <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Preview</div>
          <div className="border border-zinc-200 bg-white rounded-md overflow-hidden">
            {loading ? (
              <div className="h-64 grid place-items-center text-zinc-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : !data ? (
              <div className="h-64 grid place-items-center text-zinc-400 text-sm">
                Preview unavailable
              </div>
            ) : type === "faq" ? (
              <div className="p-8 bg-white"><FAQView data={data} /></div>
            ) : type === "richtext" ? (
              <div className="p-8 bg-white"><RichTextView data={data} /></div>
            ) : type === "gallery" ? (
              <div className="p-8 bg-white"><GallerySuccessPreview data={data} /></div>
            ) : (
              <div className="p-8 bg-white">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-green-500 animate-pulse" />
                  <div>
                    <div className="font-display text-lg font-medium text-zinc-950">{data.name}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">
                      {data.hit_count} hit{data.hit_count === 1 ? "" : "s"} logged ·
                      {data.ga_measurement_id
                        ? ` Forwarding to ${data.ga_measurement_id}${data.has_api_secret ? "" : " (no API secret — local only)"}`
                        : " Local logging only"}
                    </div>
                  </div>
                </div>
                <img src={urls.embed} alt="pixel" width="1" height="1" style={{ display: "none" }} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function GallerySuccessPreview({ data }) {
  const [mediaMap, setMediaMap] = useState({});
  useEffect(() => {
    listMedia().then((m) => {
      const map = {};
      for (const it of (m.media || [])) map[it.id] = { ...it, url: `${BACKEND_URL}/api/media/${it.id}.${it.ext}` };
      setMediaMap(map);
    }).catch(() => {});
  }, []);
  return <GalleryView data={data} mediaMap={mediaMap} />;
}

